/**
 * useChatWindowEvents — the window-event listener effects for ChatBox:
 * xavior:send-chat-message, xavior:log-chat-message, xavior:module-archived,
 * xavior:force-module-focus, and the xavior:revise-seed / xavior:revise-end pair
 * (Revise-with-AI seed delivery + dedupe lifecycle).
 *
 * Must be called at the same position the inline block occupied so hook
 * count/order is preserved. Refs and setters are threaded in BY REFERENCE so
 * reads/writes stay shared with ChatBox.
 */
import { useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { UseChatReturn } from '@/features/chat/hooks/useChat.types';
import type { Conversation } from '@/types/conversations';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';

/**
 * Neutralize attacker-influenced text (recipient / subject / body drawn from the
 * incoming email) before it is interpolated AFTER the authoritative
 * `<!-- revise-target … -->` marker in a Revise seed: strip HTML-comment
 * delimiters entirely and break the literal `revise-target` token (non-breaking
 * hyphen — visually faithful, but not the literal ASCII token). A crafted subject
 * or body can otherwise forge a SECOND marker into this trusted assistant turn;
 * this keeps the real marker provably the only `revise-target` line.
 */
function neutralizeMarkerTokens(value: string): string {
  return value
    .replace(/<!--/g, '')
    .replace(/-->/g, '')
    .replace(/revise-target/gi, 'revise‑target'); // U+2011 non-breaking hyphen: same glyph, not the literal token
}

export interface UseChatWindowEventsDeps {
  activeConversation: Conversation | null;
  /** Most-recently-created conversation id (ConversationContext) — the deterministic revise-seed target. */
  lastCreatedConversationId: string | null;
  /**
   * The ORIGIN (temp, `conversation-*`) id that resolved into `lastCreatedConversationId`
   * (ConversationContext) — i.e. the id the create that just resolved was invoked with. This is the
   * authoritative correlation key for matching a confirmed real conversation id back to its OWN
   * pending revise seed, independent of resolution order (no FIFO assumption — #5412 residual).
   */
  lastCreatedConversationOriginId: string | null;
  sendMessage: UseChatReturn['sendMessage'];
  appendUserMessage: UseChatReturn['appendUserMessage'];
  messages: ChatMessage[];
  applyUiActions: (actions: ChatUIAction[]) => void;
  userHasScrolledRef: MutableRefObject<boolean>;
  lastRouteModuleFocusKeyRef: MutableRefObject<string | null>;
  setArchivedInlineModuleIds: Dispatch<SetStateAction<Set<string>>>;
  addAssistantMessage: UseChatReturn['addAssistantMessage'];
  markConversationGreeted: (conversationId: string) => void;
  /**
   * Current Revise-session active approval id (ReviseSessionContext) — gates the
   * `setReviseConversationId` swap-in below to the session THIS confirmed seed belongs to, so a
   * lagging second create can't clobber a DIFFERENT (or already-ended) session's tracked id.
   */
  activeReviseApprovalId: string | null;
  /** Swap the real conversation id into ReviseSessionContext once the seed's create resolves. */
  setReviseConversationId: (conversationId: string) => void;
}

export function useChatWindowEvents(deps: UseChatWindowEventsDeps): void {
  const {
    activeConversation,
    lastCreatedConversationId,
    lastCreatedConversationOriginId,
    sendMessage,
    appendUserMessage,
    messages,
    applyUiActions,
    userHasScrolledRef,
    lastRouteModuleFocusKeyRef,
    setArchivedInlineModuleIds,
    addAssistantMessage,
    markConversationGreeted,
    activeReviseApprovalId,
    setReviseConversationId,
  } = deps;

  // Listen for external send-chat-message events (e.g., from CRM Draft Email
  // button, or the Daily Summary quick-capture). `options` lets callers run the
  // turn silently (hideUserMessage/hideAssistantResponse) so the agent acts —
  // e.g. creating a calendar event/task — without cluttering the chat thread.
  // `themeId`, `brandKitId`, and `themeStyle` are threaded through to the
  // /chat POST body so the agent can apply the user's chosen theme + brand kit
  // to the build (including resolved token values for all formats).
  // `artifactTypeId` (#5365) is the Studio pipeline's PICKED catalog type id —
  // threaded the same way so the agent applies that type's skeleton
  // deterministically instead of guessing the type from the message text.
  // `context` (e.g. mentionedObjects for a picked source doc/contact) and the
  // attachment payloads (`images`/`documents`/`pastedAttachments`) let the
  // Studio pipeline send its source material WITH the build request
  // (#5309/#5310/#5311) — they map onto sendMessage's existing channels.
  useEffect(() => {
    const handleSendChatMessage = (
      event: CustomEvent<{
        message: string;
        options?: { hideUserMessage?: boolean; hideAssistantResponse?: boolean };
        themeId?: string;
        brandKitId?: string;
        artifactTypeId?: string;
        themeStyle?: { accent?: string; ink?: string; bg?: string; displayFont?: string; bodyFont?: string; logoUrl?: string };
        context?: Record<string, unknown>;
        images?: Array<{ mediaType: string; data: string; filename: string }>;
        documents?: Array<{ mediaType: string; data: string; filename: string }>;
        pastedAttachments?: Array<{ content: string; label: string; size: number }>;
      }>,
    ) => {
      const { message, options, themeId, brandKitId, artifactTypeId, themeStyle, context, images, documents, pastedAttachments } = event.detail;
      if (message && activeConversation?.id) {
        sendMessage(message, activeConversation.id, undefined, context, {
          ...options,
          ...(themeId !== undefined ? { themeId } : {}),
          ...(brandKitId !== undefined ? { brandKitId } : {}),
          ...(artifactTypeId !== undefined ? { artifactTypeId } : {}),
          ...(themeStyle !== undefined ? { themeStyle } : {}),
          ...(images && images.length > 0 ? { images } : {}),
          ...(documents && documents.length > 0 ? { documents } : {}),
          ...(pastedAttachments && pastedAttachments.length > 0 ? { pastedAttachments } : {}),
        });
      }
    };

    window.addEventListener('xavior:send-chat-message', handleSendChatMessage as EventListener);
    return () => {
      window.removeEventListener('xavior:send-chat-message', handleSendChatMessage as EventListener);
    };
  }, [activeConversation?.id, sendMessage]);

  // (The popup-era 'integration_oauth_complete' localStorage bridge was removed
  // with the OAuth popup. Connect is now a same-window redirect, so the page
  // reloads on return: the refreshed session + useOnboardingDiscovery drive the
  // post-connect onboarding state, and App.tsx's OAuth-return handler owns the
  // post-connect signals. Follow-up: a non-onboarding chat-initiated connect no
  // longer posts an "I just connected Gmail" nudge — reintroduce via a return
  // marker if the silent-after-connect chat case resurfaces.)

  // Listen for log-only chat messages (appear in UI but don't trigger agent)
  useEffect(() => {
    const handleLogChatMessage = (event: CustomEvent<{ message: string; moduleId?: string }>) => {
      const { message, moduleId } = event.detail;
      if (!message || !activeConversation?.id) return;

      // Don't add the same module back to back
      if (moduleId) {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.inlineModuleId === moduleId) return;
      }

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user' as const,
        content: message,
        timestamp: new Date(),
        inlineModuleId: moduleId,
      };
      // Force scroll to bottom so user sees the newly added module
      userHasScrolledRef.current = false;
      // Noise reduction: replace previous module navigation messages instead
      // of stacking them. Only the most recent "Show X" matters.
      // Chat may contain pairs: [user "Show X" + assistant ack], so strip both.
      appendUserMessage((prev) => {
        let trimmed = prev;
        while (trimmed.length > 0) {
          const last = trimmed[trimmed.length - 1];
          // Remove assistant ack for a view command
          if (last?.role === 'assistant' && last.routing?.startsWith('command:view')) {
            trimmed = trimmed.slice(0, -1);
            continue;
          }
          // Remove user "Show X" navigation message
          if (last?.inlineModuleId) {
            trimmed = trimmed.slice(0, -1);
            continue;
          }
          break;
        }
        return [...trimmed, userMessage];
      });
    };

    window.addEventListener('xavior:log-chat-message', handleLogChatMessage as EventListener);
    return () => {
      window.removeEventListener('xavior:log-chat-message', handleLogChatMessage as EventListener);
    };
  }, [activeConversation?.id, appendUserMessage, messages]);

  // Listen for module-archived events from sidebar to hide inline modules
  useEffect(() => {
    const handleModuleArchived = (event: CustomEvent<{ moduleId: string }>) => {
      const { moduleId } = event.detail;
      if (moduleId) {
        setArchivedInlineModuleIds((prev) => new Set(prev).add(moduleId));
      }
    };
    window.addEventListener('xavior:module-archived', handleModuleArchived as EventListener);
    return () => {
      window.removeEventListener('xavior:module-archived', handleModuleArchived as EventListener);
    };
  }, []);

  // Listen for force-module-focus events from sidebar (bypasses dedup)
  useEffect(() => {
    const handleForceModuleFocus = (event: CustomEvent<{ moduleId: string }>) => {
      const { moduleId } = event.detail;
      if (!moduleId || !activeConversation?.id) return;
      lastRouteModuleFocusKeyRef.current = null;
      applyUiActions([{
        type: 'module.focus',
        moduleId,
        conversationId: activeConversation.id,
      }]);
    };
    window.addEventListener('xavior:force-module-focus', handleForceModuleFocus as EventListener);
    return () => {
      window.removeEventListener('xavior:force-module-focus', handleForceModuleFocus as EventListener);
    };
  }, [activeConversation?.id, applyUiActions]);

  // Listen for xavior:revise-seed events — seeds ONE persisted assistant turn (the current draft +
  // a hidden revise-target marker) into the Revise chat THIS card created. The card dispatches the
  // seed synchronously in the same click that calls createConversation, so at dispatch time the
  // temp→real swap has NOT flushed: `activeConversation` is still the previously-active conversation
  // (a warm dock's real id — the common case), and the user can even manually switch to ANOTHER
  // pre-existing conversation during the ~500ms create round-trip. Gate DETERMINISTICALLY on the
  // created conversation's identity: seed only when the current id is a real (non-temp) id that
  // equals `lastCreatedConversationId`. `lastCreatedConversationId` only ever holds a
  // freshly-created conversation id (never one the user merely switched to), so a pre-existing
  // conversation can never receive a seed.
  //
  // Keyed by ORIGIN tempId (Map<tempId, PendingReviseSeed>) — NOT approvalId, and NOT a FIFO
  // "oldest pending seed whose baseline changed" guess. `lastCreatedConversationOriginId`
  // (ConversationContext) is the tempId THIS confirmed creation actually resolved from — threaded
  // through `createConversation`'s onSuccess closure — so a confirmed real id is matched to its OWN
  // seed by that origin id directly. This makes seeding correct regardless of resolution order: two
  // pending revises (temp-A, temp-B) resolving in EITHER order each land in their own conversation
  // (#5412 residual — the prior FIFO-by-dispatch-order matching broke when the SECOND create
  // resolved before the FIRST). ReviseSessionContext.beginRevise is now strict single-flight (§7.5,
  // concurrency-review root fix) — a second revise cannot begin while one is active, on ANY draft —
  // so two pending map entries only ever coexist transiently: A is explicitly closed (Send/Save &
  // send/Discard, purging its dedupe below) before B can begin, but A's OWN create promise, already
  // in flight before the close, keeps resolving independently in the background. This origin-keyed map
  // (plus the dedupe-purge below) is what keeps that late resolution from misdelivering into B's chat.
  //
  // Dedupe by approvalId (seededApprovalIdsRef) so a re-render never double-seeds; xavior:revise-end
  // (dispatched by ReviseSessionContext.endRevise on decide/close/switch, and on a failed create —
  // #5412/#5424) clears the ending approval's dedupe entry AND drops any pending seed(s) still
  // carrying that approvalId (e.g. an abandoned tempId whose create hasn't resolved yet), so a
  // re-revise on the SAME draft (Discard-then-re-revise) seeds again instead of being silently treated
  // as already-seeded, and an abandoned pending seed can never later land in a conversation nobody is
  // looking at (#5412/#5424 residual). ConversationContext's `shouldActivateOnResolve` (threaded from
  // EmailDraftReviewCard) is the complementary half — it keeps that same late-resolving abandoned
  // create from force-navigating the view away from wherever the user has since moved on to.
  interface PendingReviseSeed {
    approvalId: string;
    tempId: string;
    to: string;
    subject: string;
    body: string;
  }
  const pendingReviseSeedsRef = useRef<Map<string, PendingReviseSeed>>(new Map());
  const seededApprovalIdsRef = useRef<Set<string>>(new Set());
  // The last lastCreatedConversationOriginId value already matched to a seed — prevents a later,
  // unrelated effect re-run (same confirmed origin id, different dependency changed) from re-matching
  // a creation that has already been consumed.
  const lastConsumedOriginIdRef = useRef<string | null>(null);

  useEffect(() => {
    const cid = activeConversation?.id ?? null;

    const onReviseSeed = (
      event: CustomEvent<{ approvalId: string; tempId: string; to: string; subject: string; body: string }>,
    ) => {
      pendingReviseSeedsRef.current.set(event.detail.tempId, { ...event.detail });
      trySeedRevise();
    };
    const onReviseEnd = (event: CustomEvent<{ approvalId: string }>) => {
      seededApprovalIdsRef.current.delete(event.detail.approvalId);
      for (const [tempId, seed] of pendingReviseSeedsRef.current) {
        if (seed.approvalId === event.detail.approvalId) {
          pendingReviseSeedsRef.current.delete(tempId);
        }
      }
    };
    const trySeedRevise = () => {
      if (!cid) return;
      // Seed ONLY into the conversation a create produced: a real (non-temp) id that is now the
      // last-created conversation.
      if (cid.startsWith('conversation-')) return;
      if (cid !== lastCreatedConversationId) return;
      if (!lastCreatedConversationOriginId) return;
      // This exact confirmed origin was already matched to a seed on a prior run — nothing new to do.
      if (lastCreatedConversationOriginId === lastConsumedOriginIdRef.current) return;

      const seed = pendingReviseSeedsRef.current.get(lastCreatedConversationOriginId);
      if (!seed) return; // this creation isn't one a pending revise seed is waiting on (e.g. a plain "New chat")

      lastConsumedOriginIdRef.current = lastCreatedConversationOriginId;
      pendingReviseSeedsRef.current.delete(lastCreatedConversationOriginId);
      if (seededApprovalIdsRef.current.has(seed.approvalId)) return; // already seeded — defensive, dedupe already prevents this
      seededApprovalIdsRef.current.add(seed.approvalId);

      markConversationGreeted(cid); // skip the canned "new chat" greeting for this revise chat
      // The active revise session's tracked conversation id starts as the temp id (beginRevise);
      // swap in the real one now that this seed's create is confirmed — but only for the
      // CURRENTLY active session, so a lagging second create can't clobber a different (or
      // already-ended) session's tracked id.
      if (seed.approvalId === activeReviseApprovalId) {
        setReviseConversationId(cid);
      }
      // Neutralize attacker-influenced fields so a crafted subject/body cannot forge a second marker.
      const to = neutralizeMarkerTokens(seed.to);
      const subject = neutralizeMarkerTokens(seed.subject);
      const body = neutralizeMarkerTokens(seed.body);
      const content =
        `How do you want to edit this draft? Tell me what to change — shorter, warmer, drop a paragraph, more formal — and I'll rewrite it in place.\n\n` +
        `<!-- revise-target approvalId="${seed.approvalId}" — when the user asks for a change, call revise_email_draft with this approvalId and the full rewritten body. Do NOT send. -->\n` +
        `**Current draft** — To: ${to} · Subject: ${subject}\n\n${body}`;
      addAssistantMessage(content, cid);
    };
    window.addEventListener('xavior:revise-seed', onReviseSeed as EventListener);
    window.addEventListener('xavior:revise-end', onReviseEnd as EventListener);
    trySeedRevise(); // handles the swap: this effect re-ran because lastCreatedConversation(OriginId) / the active id changed
    return () => {
      window.removeEventListener('xavior:revise-seed', onReviseSeed as EventListener);
      window.removeEventListener('xavior:revise-end', onReviseEnd as EventListener);
    };
  }, [
    activeConversation?.id,
    lastCreatedConversationId,
    lastCreatedConversationOriginId,
    activeReviseApprovalId,
    addAssistantMessage,
    markConversationGreeted,
    setReviseConversationId,
  ]);
}
